'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { FILTER_PRESETS } from '@/lib/browser-image-effects-tools';

const MAX_THUMB = 120;

export default function BrowserImageEffects() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [presetId, setPresetId] = useState('grayscale');
  const [intensity, setIntensity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  const activePreset = useMemo(
    () => FILTER_PRESETS.find((p) => p.id === presetId) ?? FILTER_PRESETS[0],
    [presetId],
  );

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
    };
  }, []);

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setError(null);
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError('That image could not be loaded.');
    img.src = url;
  }

  // Main preview + download render.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.filter = activePreset.filter(intensity);
    ctx.drawImage(image, 0, 0);
    ctx.filter = 'none';
    canvas.toBlob((blob) => {
      if (!blob) return;
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
      const url = URL.createObjectURL(blob);
      downloadUrlRef.current = url;
      setDownloadUrl(url);
    }, 'image/png');
  }, [image, activePreset, intensity]);

  // Pre-render one small thumbnail data URL per preset for the gallery buttons.
  const thumbs = useMemo<Record<string, string>>(() => {
    if (typeof document === 'undefined' || !image) return {};
    const ratio = Math.min(1, MAX_THUMB / Math.max(image.naturalWidth, image.naturalHeight));
    const w = Math.max(1, Math.round(image.naturalWidth * ratio));
    const h = Math.max(1, Math.round(image.naturalHeight * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return {};
    const map: Record<string, string> = {};
    for (const preset of FILTER_PRESETS) {
      ctx.filter = preset.filter(intensity);
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(image, 0, 0, w, h);
      ctx.filter = 'none';
      map[preset.id] = canvas.toDataURL('image/png');
    }
    return map;
  }, [image, intensity]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Image effects</span>
        <h2 className="tool-live-title">Apply photo filters and effects</h2>
        <p className="tool-live-subtitle">
          Upload an image, pick a one-click look, fine-tune the intensity, and download. Everything
          runs on an HTML canvas in your browser. Nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}

          {image ? (
            <>
              <div
                className="tool-live-field tool-live-field-full"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
                  gap: 8,
                }}
              >
                {FILTER_PRESETS.map((preset) => {
                  const src = thumbs[preset.id] ?? null;
                  const selected = preset.id === presetId;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => setPresetId(preset.id)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: 4,
                        borderRadius: 10,
                        cursor: 'pointer',
                        background: 'transparent',
                        border: selected ? '2px solid #2563eb' : '2px solid transparent',
                        outline: selected ? 'none' : '1px solid rgba(148,163,184,0.35)',
                      }}
                      type="button"
                    >
                      {src ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          alt={preset.label}
                          src={src}
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            objectFit: 'cover',
                            borderRadius: 6,
                            display: 'block',
                          }}
                        />
                      ) : null}
                      <span style={{ fontSize: 11, textAlign: 'center' }}>{preset.label}</span>
                    </button>
                  );
                })}
              </div>

              <label className="tool-live-field tool-live-field-full">
                <span>Intensity: {Math.round(intensity * 100)}%</span>
                <input
                  max={1}
                  min={0}
                  onChange={(event) => setIntensity(Number(event.target.value))}
                  step={0.02}
                  type="range"
                  value={intensity}
                />
              </label>

              <div className="tool-live-actions">
                {downloadUrl ? (
                  <a
                    className="button button-secondary"
                    download={`image-${activePreset.id}.png`}
                    href={downloadUrl}
                  >
                    Download PNG
                  </a>
                ) : null}
              </div>
            </>
          ) : (
            <p className="tool-live-panel-note">Pick an image to see the filter gallery.</p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
